/* =========================
   src/Redux/thunks/doctorsThunks.js
========================= */

import { createAsyncThunk } from "@reduxjs/toolkit";
import api, { axiosDoctorRegister } from "../../api/axios";
import { axiosDoctorRegister as axiosSharedDoctorRegister } from "../../../api/axios";
import Cookies from "js-cookie";
import { resetDoctorProfile } from "../../../ProfileDoctor/redux/slices/doctorProfileSlice";
import { clearSessionLogout } from "../../../auth/sessionLogout";

// ✅ FIX : ne plus stocker sous "accessToken" (clé patient)
// pour éviter que isDoctorSession() détecte un faux token patient
const persistDoctorAuthToken = (token) => {
  clearSessionLogout();
  if (!token) return;

  const value = String(token);
  Cookies.set("doctorAccessToken", value);
  Cookies.set("doctorToken", value);
  localStorage.setItem("doctorAccessToken", value);
  localStorage.setItem("doctorToken", value);

  // ✅ Marquer le rôle pour la redirection après expiration
  localStorage.setItem("userRole", "doctor");
  // sessionStorage is isolated per browser tab (unlike localStorage, which
  // is shared across every tab of the same origin). A doctor and patient
  // logged in in separate tabs of the same browser would otherwise
  // overwrite each other's "userRole" and get misclassified. This is the
  // reliable, tab-scoped signal isDoctorSession() checks first, and it's
  // reasserted every time this doctor page loads (self-healing).
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem("activeSessionRole", "doctor");
  }

  // NOTE: this used to also purge the patient's accessToken/auth_token/token
  // cookies here ("clear a leftover patient session"). Cookies and
  // localStorage are shared across every tab of the same browser origin, so
  // that purge ran every time this doctor page mounted and silently logged
  // out a patient session open in another tab of the same browser — exactly
  // the scenario this app supports (testing both roles side by side).
  // attachAuthHeader() already picks the correct token per request (doctor
  // vs patient) via isDoctorScopedRequest/isDoctorSession(), so this purge
  // was redundant with that logic and only harmful for multi-tab use.
};

const FORBIDDEN_UPDATE_FIELDS = new Set([
  "role",
  "userRole",
  "id",
  "_id",
  "userId",
  "doctorId",
  "medecinId",
  "patientId",
  "uuid",
  "pgId",
  "avatarUrl",
  "type",
  "status",
  "createdAt",
  "updatedAt",
  "raw",
]);

export const sanitizeDoctorUpdateValues = (values = {}) => {
  if (!values) return {};

  if (typeof FormData !== "undefined" && values instanceof FormData) {
    const next = new FormData();
    Array.from(values.entries()).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (FORBIDDEN_UPDATE_FIELDS.has(String(key))) return;
      next.append(key, value);
    });
    return next;
  }

  if (typeof values !== "object") return {};

  return Object.fromEntries(
    Object.entries(values).filter(([key, value]) => {
      if (value === undefined || value === null) return false;
      if (FORBIDDEN_UPDATE_FIELDS.has(String(key))) return false;
      return true;
    })
  );
};

/* ========================================================
   REGISTER DOCTOR
======================================================== */
export const registerDoctor = createAsyncThunk(
  "doctors/registerDoctor",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axiosDoctorRegister.post("/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Registration failed"
      );
    }
  }
);

/* ========================================================
   LOGIN DOCTOR
   gateway: POST /auth/doctor/login-dr
======================================================== */
export const loginDoctor = createAsyncThunk(
  "doctors/loginDoctor",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/auth/doctor/login-dr",
        { email, password },
        { withCredentials: true }
      );

      const data = res.data || {};
      const doctor = data.doctor || null;

      if (doctor?.email) {
        Cookies.set(
          "doctorSession",
          JSON.stringify({ user: doctor, email: doctor.email })
        );
      }

      if (data.accessToken) {
        persistDoctorAuthToken(data.accessToken);
      }

      // ✅ Si le compte est rejected, poser un flag pour ouvrir la modal une seule fois au login
      if (doctor?.status === 'rejected') {
        sessionStorage.setItem('showRejectedModal', 'true');
      }

      return {
        accessToken: data.accessToken || null,
        refreshToken: data.refreshToken || null,
        doctor,
        email: data.email || doctor?.email || email || "",
        isVerified: !!(data.isVerified ?? doctor?.isVerified),
        codeSent: !!(data.codeSent || data.step === "2fa" || data.requires2FA),
        message: data.message || "",
      };
    } catch (err) {
      const backendMsg = err?.response?.data?.message;
      let msg = backendMsg || "Login failed : Email or password incorrect!";

      if (
        typeof backendMsg === "string" &&
        (backendMsg.toLowerCase().includes("invalid") ||
          backendMsg.toLowerCase().includes("incorrect") ||
          backendMsg.toLowerCase().includes("credentials") ||
          backendMsg.toLowerCase().includes("password") ||
          backendMsg.toLowerCase().includes("email"))
      ) {
        msg = "Email ou mot de passe erroné.";
      }

      return rejectWithValue(msg);
    }
  }
);

/* ========================================================
   VERIFY CODE
   gateway: POST /auth/doctor/verify
======================================================== */
export const verifyCodeDoctor = createAsyncThunk(
  "doctors/verifyCode",
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/auth/doctor/verify",
        { email, code },
        { withCredentials: true, skipAuth: true }
      );

      const data = res.data || {};

      if (data.accessToken) {
        persistDoctorAuthToken(data.accessToken);
      }

      if (data.doctor?.email) {
        Cookies.set(
          "doctorSession",
          JSON.stringify({ user: data.doctor, email: data.doctor.email })
        );
      }

      // ✅ Flag rejected après vérification 2FA aussi
      if (data.doctor?.status === 'rejected') {
        sessionStorage.setItem('showRejectedModal', 'true');
      }

      return {
        user: data.doctor || null,
        doctor: data.doctor || null,
        accessToken: data.accessToken || null,
        refreshToken: data.refreshToken || null,
        isVerified: true,
        message: data.message || "",
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Verification failed"
      );
    }
  }
);

/* ========================================================
   RESEND CODE
   gateway: POST /auth/doctor/resend-code
======================================================== */
export const resendCodeDoctor = createAsyncThunk(
  "doctors/resendCode",
  async (email, { rejectWithValue }) => {
    try {
      const normalizedEmail =
        typeof email === "string" ? email.trim().toLowerCase() : "";

      const res = await api.post(
        "/auth/doctor/resend-code",
        { email: normalizedEmail },
        { withCredentials: true }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to resend code"
      );
    }
  }
);

/* ========================================================
   REQUEST PASSWORD RESET
   gateway: POST /auth/doctor/request-reset
======================================================== */
export const requestResetDoctor = createAsyncThunk(
  "doctors/requestReset",
  async (email, { rejectWithValue }) => {
    try {
      const normalizedEmail =
        typeof email === "string" ? email.trim().toLowerCase() : "";

      const res = await api.post(
        "/auth/doctor/request-reset",
        { email: normalizedEmail },
        { withCredentials: true }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to request reset"
      );
    }
  }
);

/* ========================================================
   RESET PASSWORD
   gateway: POST /auth/doctor/reset-password
======================================================== */
export const resetPasswordDoctor = createAsyncThunk(
  "doctors/resetPassword",
  async (
    { email, code, newPassword, confirmNewPassword },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        "/auth/doctor/reset-password",
        {
          email: String(email || "").trim().toLowerCase(),
          code: String(code || "").trim(),
          newPassword,
          confirmNewPassword,
        },
        { withCredentials: true }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Password reset failed"
      );
    }
  }
);

/* ========================================================
   GET CURRENT DOCTOR
======================================================== */
export const getCurrentDoctor = createAsyncThunk(
  "doctors/getCurrentDoctor",
  async (_, { rejectWithValue }) => {
    try {
      // ✅ FIX : lire doctorAccessToken, pas accessToken
      const token =
        Cookies.get("doctorAccessToken") ||
        Cookies.get("doctorToken") ||
        localStorage.getItem("doctorAccessToken") ||
        localStorage.getItem("doctorToken") ||
        null;

      const config = {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      };

      const res = await axiosDoctorRegister.get("/me", config);
      const doctor = res.data || {};

      if (token) {
        persistDoctorAuthToken(token);
      }


      const rawExperience =
        doctor.yearsOfExperience ??
        doctor.experienceYears ??
        doctor.experience ??
        null;

      const yearsOfExperienceNormalized =
        rawExperience !== null &&
        rawExperience !== undefined &&
        rawExperience !== ""
          ? parseInt(String(rawExperience), 10)
          : null;

      const toSafeInt = (v) => {
        if (v == null) return null;
        const n = Number(String(v).trim());
        return Number.isFinite(n) && n > 0 ? n : null;
      };

      const pgId =
        toSafeInt(doctor.pgId) ??
        toSafeInt(doctor.id) ??
        toSafeInt(doctor.doctorId) ??
        toSafeInt(doctor.userId) ??
        null;

      const stringId = String(
        doctor.id ?? doctor._id ?? doctor.doctorId ?? doctor.userId ?? ""
      );

      return {
        id:       stringId,
        pgId,
        doctorId: pgId,
        fullName:
          doctor.fullName ||
          `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim(),
        email:    doctor.email || "",
        phone:    doctor.phone || "",
        specialty: doctor.specialty || "",
        city:    doctor.city || "",
        country: doctor.country || "",
        birthDate: doctor.birthDate || "",

        // ✅ Statut de validation admin (pending | approved | rejected)
        status:          doctor.status || "pending",
        validatedAt:     doctor.validatedAt || null,
        rejectionReason: doctor.rejectionReason || null,

        yearsOfExperience: Number.isFinite(yearsOfExperienceNormalized)
          ? yearsOfExperienceNormalized
          : null,

        experience: Number.isFinite(yearsOfExperienceNormalized)
          ? yearsOfExperienceNormalized
          : null,

        clinicAddress: doctor.clinicAddress || doctor.practiceAddress || "",
        practicePlace: doctor.practicePlace || "",

        avatar: doctor.avatar || doctor.photo || null,
        appointmentReminders: doctor.appointmentReminders ?? true,
        medicationReminders:  doctor.medicationReminders ?? true,
        messageAlerts:        doctor.messageAlerts ?? true,
        questionUpdates:      doctor.questionUpdates ?? true,
        useLocation:          doctor.useLocation ?? true,

        raw: doctor,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch current doctor"
      );
    }
  }
);

/* ========================================================
   LOGOUT DOCTOR
   gateway: POST /auth/doctor/logout
======================================================== */
export const logoutDoctor = createAsyncThunk(
  "doctors/logoutDoctor",
  async (doctorId, { dispatch }) => {
    try {
      await api.post(
        "/auth/doctor/logout",
        { doctorId },
        { withCredentials: true }
      );
    } catch (_) {
      // ignore network/logout errors
    } finally {
      // ✅ FIX : nettoyer tous les tokens médecin + le rôle
      Cookies.remove("doctorSession");
      Cookies.remove("doctorAccessToken");
      Cookies.remove("doctorToken");
      Cookies.remove("accessToken");
      localStorage.removeItem("doctorAccessToken");
      localStorage.removeItem("doctorToken");
      localStorage.removeItem("userRole");
      dispatch(resetDoctorProfile());
    }

    return true;
  }
);

/* ========================================================
   UPDATE DOCTOR
======================================================== */
export const updateDoctor = createAsyncThunk(
  "doctors/updateDoctor",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      if (!id) return rejectWithValue("Doctor ID is required for update");

      const sanitizedFormData = sanitizeDoctorUpdateValues(formData);

      const res = await axiosDoctorRegister.patch(`/${id}`, sanitizedFormData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update doctor"
      );
    }
  }
);

/* ========================================================
   UPDATE yearsOfExperience ONLY
======================================================== */
export const updateDoctorYearsOfExperience = createAsyncThunk(
  "doctors/updateDoctorYearsOfExperience",
  async ({ id, yearsOfExperience }, { dispatch, rejectWithValue }) => {
    try {
      if (!id) return rejectWithValue("Doctor ID is required");

      const fd = new FormData();
      fd.append("yearsOfExperience", String(yearsOfExperience ?? "").trim());

      const res = await dispatch(updateDoctor({ id, formData: fd })).unwrap();
      return res;
    } catch (err) {
      return rejectWithValue(
        err?.message || "Failed to update yearsOfExperience"
      );
    }
  }
);

/* ========================================================
   DELETE DOCTOR
======================================================== */
export const deleteDoctor = createAsyncThunk(
  "doctors/deleteDoctor",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosDoctorRegister.delete(`/${id}`, {
        withCredentials: true,
      });
      return res.data?.message || "Doctor deleted successfully";
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete doctor"
      );
    }
  }
);

/* ========================================================
   FETCH ALL DOCTORS
======================================================== */
export const fetchDoctors = createAsyncThunk(
  "doctors/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosSharedDoctorRegister.get("/", {
        withCredentials: true,
      });
      const rows = Array.isArray(res.data) ? res.data : res.data?.data || [];

      return rows.map((d) => ({
        id: String(d.id ?? d._id ?? d.doctorId ?? d.userId),
        fullName: d.fullName || d.name || "",
        email: d.email || "",
        specialty: d.specialty || "",
        city: d.city || "",
        avatar: d.avatar || d.photo || null,
        phone: d.phone || "",
        clinicAddress: d.clinicAddress || d.practiceAddress || "",
        lat: d.location?.lat ?? d.lat ?? d.coordinates?.lat ?? null,
        lng: d.location?.lng ?? d.lng ?? d.coordinates?.lng ?? null,
        location: d.location || null,

        // ✅ FIX : garder le statut de validation admin, sinon ProvidersList
        // ne peut jamais savoir quel docteur est "confirmed"/"approved"
        status: d.status || d.validationStatus || d.accountStatus || "pending",
        validationStatus: d.validationStatus || null,
        accountStatus: d.accountStatus || null,
        isVerified: d.isVerified ?? d.verified ?? d.isActive ?? null,

        raw: d,
      }));
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors du chargement des docteurs"
      );
    }
  }
);

/* ========================================================
   FETCH DOCTOR BY ID
======================================================== */
export const fetchDoctorById = createAsyncThunk(
  "doctors/fetchById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await axiosDoctorRegister.get(
        `/${encodeURIComponent(String(id))}`,
        { withCredentials: true }
      );

      const asArr = (x) => (Array.isArray(x) ? x : x ? [x] : []);
      const safe = (v) => (v == null ? "" : String(v).trim());
      const pick = (...vals) => vals.find((v) => safe(v) !== "");
      const doctorId = pick(
        data?.doctorId,
        data?.id,
        data?._id,
        data?.userId,
        data?.pgId,
        id
      );

      const clinicName = pick(
        data?.clinic?.name,
        data?.clinicName,
        data?.clinic,
        data?.practicePlace,
        data?.hospitalName
      );

      const address = pick(
        data?.clinic?.address,
        data?.clinic?.clinicAddress,
        data?.address,
        data?.clinicAddress,
        data?.practiceAddress,
        data?.location?.address
      );

      const officeHours = pick(
        data?.officeHours,
        data?.hours,
        data?.clinic?.officeHours,
        data?.clinic?.hours
      );

      const yearsOfExperience = pick(
        data?.yearsOfExperience,
        data?.experienceYears,
        data?.experience,
        data?.clinic?.yearsOfExperience
      );

      const languages = asArr(
        pick(data?.languages, data?.spokenLanguages, data?.clinic?.languages)
      );

      const certifications = asArr(
        pick(
          data?.certifications,
          data?.diplomas,
          data?.clinic?.certifications
        )
      );

      return {
        ...data,
        id: String(doctorId),
        clinicName: clinicName || "",
        address: address || "",
        officeHours: officeHours || "",
        yearsOfExperience: yearsOfExperience ?? null,
        languages,
        certifications,
        raw: data,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur profil médecin"
      );
    }
  }
);

/* ========================================================
   SEARCH DOCTORS BY NAME
======================================================== */
export const searchDoctorsByName = createAsyncThunk(
  "doctor/searchDoctorsByName",
  async (fullName, { rejectWithValue }) => {
    try {
      const q = encodeURIComponent(String(fullName || ""));
      const { data } = await axiosDoctorRegister.get(`/search/${q}`, {
        withCredentials: true,
      });
      const rows = Array.isArray(data) ? data : data?.data || [];

      return rows.map((d) => ({
        id: String(d.id ?? d._id ?? d.doctorId ?? d.userId),
        fullName: d.fullName || d.name || "",
        email: d.email || "",
        specialty: d.specialty || "",
        city: d.city || "",
        avatar: d.avatar || d.photo || null,
      }));
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur recherche"
      );
    }
  }
);